import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ElementRef,
    ViewEncapsulation,
    AfterViewInit,
    HostBinding,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { ICommentModel } from '@fi-shared/fi-comments/fi-comments';
import { BehaviorSubject, delay, filter, Observable, Subject, takeUntil } from 'rxjs';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { FiAttachmentDisplaySize, ObjectTypes } from '@fi-shared/types';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { AppTextService } from '@fi-shared/app-text';
import { Animations } from '@fi-shared/utils/animations';
import { FiCommentsListStateService } from '../services/fi-comments-list-state.service';
import { FiCommentsListHelperService } from '../services/fi-comments-list-helper.service';
import { TextEditorTagUsersService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-tag-users.service';

const fadeHeightAnimationTiming: number = 200;
const smoothConnectionLineUpdateInterval: number = 20;

@Component({
    selector: 'fi-comments-list-item',
    templateUrl: './fi-comments-list-item.component.html',
    styleUrls: ['./fi-comments-list-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-comments-list-item' },
    standalone: false,
    animations: [Animations.fadeHeight(fadeHeightAnimationTiming)],
})
export class FiCommentsListItemComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input({ required: true }) objectId: number;
    @Input({ required: true }) objectType: ObjectTypes;
    @Input({ required: true }) instanceId: string;
    @Input({ required: true }) comment: ICommentModel;

    @Input() hasDeleteAccessOfOwnerItem: boolean = false;
    @Input() isDisabled: boolean = false;
    @Input() isCommentFooterHidden: boolean = false;

    @HostBinding('class.extra-margin')
    @Input()
    addChildrenOffset: boolean = false;

    @Input() isChildrenVisible: boolean = false;
    @Input() isMainComment: boolean = false;
    @Input() attachmentsDisplaySize: FiAttachmentDisplaySize = FiAttachmentDisplaySize.Medium;

    @HostBinding('class.simplified-layout')
    @Input()
    isSimplifiedLayout: boolean = false;

    @Input() expandCommentReplies$: Observable<number | undefined>;

    @Output() onEditToggle = new EventEmitter<void>();
    @Output() replyToggle = new EventEmitter<void>();

    protected showEditor$ = new BehaviorSubject<boolean>(false);
    protected showReply$ = new BehaviorSubject<boolean>(false);
    protected commentCopyForEdit: ICommentModel;

    protected readonly ObjectTypes = ObjectTypes;
    protected readonly isDefined = isDefined;

    private connectionLineUpdateIntervalId: number | null = null;
    private connectionLineTargetElement: HTMLElement;

    constructor(
        private cdr: ChangeDetectorRef,
        private elementRef: ElementRef,
        private appTextService: AppTextService,
        private commentsListStateService: FiCommentsListStateService,
        private commentsListHelperService: FiCommentsListHelperService,
        private textEditorTagUsersService: TextEditorTagUsersService,
    ) {}

    public ngAfterViewInit(): void {
        this.setConnectionTargetElement();
        this.setCommentsConnectionLine();
    }

    public ngOnInit(): void {
        this.subscribeOnCommentsUpdated();
        this.subscribeOnExpandCommentReplies();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes.comment;
        if (change) {
            this.updateContentWithFixedUserProfileTags();
        }
    }

    private subscribeOnExpandCommentReplies(): void {
        this.expandCommentReplies$?.pipe(takeUntil(this.ngOnDestroy$)).subscribe((parentCommentId) => {
            if (parentCommentId === this.comment.Id) {
                this.isChildrenVisible = true;
                this.cdr.detectChanges();
                this.startSmoothConnectionLineUpdate();
            }
        });
    }

    private updateContentWithFixedUserProfileTags() {
        const updatedContent = this.textEditorTagUsersService.getContentWithFixedUserProfileTags(
            this.comment.Content,
            this.comment.TaggedUsers,
        );
        this.comment.Content = updatedContent;
    }

    private isCurrentCommentUpdated(commentId: number): boolean {
        return commentId === this.comment.Id || !isDefined(commentId);
    }

    private isCurrentCommentCategory(objectType: ObjectTypes, objectId: number): boolean {
        return objectType === this.objectType && objectId === this.objectId;
    }

    private subscribeOnCommentsUpdated(): void {
        this.commentsListStateService.commentsUpdated$
            .pipe(
                takeUntil(this.ngOnDestroy$),
                filter((commentId) => this.isCurrentCommentUpdated(commentId)),
                delay(fadeHeightAnimationTiming),
            )
            .subscribe(() => {
                this.updateCommentConnectionLine({
                    //commentUpdated: true,
                });
            });

        this.commentsListStateService.commentUpdateConnectionLine$
            .pipe(
                takeUntil(this.ngOnDestroy$),
                filter(({ objectType, objectId }) => this.isCurrentCommentCategory(objectType, objectId)),
            )
            .subscribe(() => {
                this.updateCommentConnectionLine({ skipChildrenVisibilityUpdate: true });
            });
    }

    private updateCommentConnectionLine(args?: {
        skipChildrenVisibilityUpdate?: boolean;
        //commentUpdated?: boolean;
    }): void {
        if (!args) {
            args = {};
        }
        const updatedComment = this.commentsListStateService.getCommentById(
            this.comment.Id,
            this.objectType,
            this.objectId,
        );
        if (isDefined(updatedComment)) {
            this.comment = updatedComment;

            /*if (args.commentUpdated) {
                const updatedContent = this.textEditorTagUsersService.getConvertedFromEditToViewModeUserTags(
                    this.comment.Content,
                    {
                        addContentToUserTagsElements: true,
                        taggedUsers: this.comment.TaggedUsers,
                    },
                );
                this.comment = { ...this.comment, Content: updatedContent };
            }*/

            if (!this.isChildrenVisible && this.getTotalChildrenCount() > 0 && !args.skipChildrenVisibilityUpdate) {
                this.isChildrenVisible = true;
            }

            this.cdr.markForCheck();

            this.setConnectionTargetElement();
            if (this.connectionLineTargetElement) {
                this.startSmoothConnectionLineUpdate();
            } else {
                this.hideConnectionLine();
            }
        }
    }

    private hideConnectionLine(): void {
        this.elementRef.nativeElement.style.setProperty('--comments-connection-line-display', 'none');
    }

    protected getLoadMoreCommentsButtonId(): string {
        return this.commentsListHelperService.getLoadMoreCommentsButtonId(this.comment.Id);
    }

    protected openEditor(): void {
        this.commentCopyForEdit = cloneDeep(this.comment);
        this.showEditor$.next(true);
        this.onChildCommentEditToggle();
    }

    protected onChildCommentEditToggle(): void {
        setTimeout(() => {
            this.onEditToggle.emit();
            this.setCommentsConnectionLine();
        }, 0);
    }

    protected onReplyToggle(): void {
        setTimeout(() => {
            this.replyToggle.emit();
            this.setCommentsConnectionLine();
        }, 0);
    }

    protected onCommentEditEdited(comment: ICommentModel): void {
        this.onChildCommentEditToggle();
    }

    protected closeEditor(): void {
        this.showEditor$.next(false);
        this.onChildCommentEditToggle();
    }

    protected toggleReply(): void {
        this.showReply$.next(!this.showReply$.value);
        this.onReplyToggle();
    }

    protected onReplySaved(): void {
        this.hideReplyContainer();
        this.isChildrenVisible = true;
        this.setConnectionTargetElement();
        this.setCommentsConnectionLine();
        this.cdr.markForCheck();
    }

    protected hideReplyContainer() {
        this.showReply$.next(false);
        this.onReplyToggle();
    }

    protected getCommentId(commentId: number): string {
        return this.commentsListHelperService.getCommentId(commentId);
    }

    private isCommentsConnectionShown(): boolean {
        return this.commentsListHelperService.isCommentsConnectionShown(this.isMainComment, this.comment);
    }

    protected setCommentsConnectionLine(): void {
        if (!this.isCommentsConnectionShown() || isNullOrUndefined(this.connectionLineTargetElement)) {
            this.hideConnectionLine();
            return;
        }
        const topOffset: number = this.getCommentsConnectionLineOffset();
        this.elementRef.nativeElement.style.setProperty('--comments-connection-line-height', `${topOffset}px`);
        this.elementRef.nativeElement.style.setProperty('--comments-connection-line-display', 'block');
    }

    protected getTotalChildrenCount(): number {
        return this.commentsListHelperService.getTotalChildrenCount(this.comment);
    }

    protected getCommentReplyButtonText(): string {
        return this.commentsListHelperService.getCommentReplyButtonText(this.isMainComment, this.comment);
    }

    protected isShowMoreCommentsButtonShown(): boolean {
        return this.commentsListHelperService.isShowMoreCommentsButtonShown(this.isMainComment, this.comment);
    }

    protected toggleChildrenVisibility(): void {
        this.isChildrenVisible = !this.isChildrenVisible;
        this.startSmoothConnectionLineUpdate();
    }

    private startSmoothConnectionLineUpdate(): void {
        if (!this.isCommentsConnectionShown()) {
            return;
        }
        if (this.connectionLineUpdateIntervalId) {
            clearInterval(this.connectionLineUpdateIntervalId);
            this.connectionLineUpdateIntervalId = null;
        }

        this.connectionLineUpdateIntervalId = window.setInterval(() => {
            this.setCommentsConnectionLine();
        }, smoothConnectionLineUpdateInterval);

        setTimeout(() => {
            if (this.connectionLineUpdateIntervalId) {
                clearInterval(this.connectionLineUpdateIntervalId);
                this.connectionLineUpdateIntervalId = null;
                setTimeout(() => {
                    this.setCommentsConnectionLine();
                }, 10);
            }
        }, fadeHeightAnimationTiming);
    }

    protected getToggleChildrenVisibilityButtonText(): string {
        if (this.isChildrenVisible) {
            return this.appTextService.get('common.hide_replies');
        }
        const repliesToShowCount = this.getTotalChildrenCount() - 1;
        return this.appTextService.get('comment.show_more_replies', { '{0}': repliesToShowCount });
    }

    protected getVisibleChildren(): ICommentModel[] {
        if (this.isChildrenVisible) {
            return this.comment.ChildItems;
        }
        return this.isMainComment ? [this.comment.ChildItems[0]] : [];
    }

    private getCommentsConnectionLineOffset(): number {
        return this.commentsListHelperService.getCommentsConnectionLineOffset(
            this.connectionLineTargetElement,
            this.comment,
            this.isMainComment,
            this.isSimplifiedLayout,
        );
    }

    private setConnectionTargetElement(): void {
        this.connectionLineTargetElement = this.commentsListHelperService.getConnectionTargetElement(
            this.isMainComment,
            this.comment,
        );
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();

        if (this.connectionLineUpdateIntervalId) {
            clearInterval(this.connectionLineUpdateIntervalId);
            this.connectionLineUpdateIntervalId = null;
        }
    }
}
